/**
 * OnHandReport.jsx — iter166
 *
 * One large stock-on-hand report across all SKUs, with:
 *   • Total SKU / unit summary cards
 *   • Live filters: search, include-zero, below-reorder
 *   • Per-SKU row showing on-hand / available / reserved / quarantine,
 *     locations breakdown, UOM equivalents (e.g. 60000 mL = 60 L = 5 case)
 *   • CSV export
 */
import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowDown, MagnifyingGlass, ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getOnHandReport, downloadOnHandReportCsv } from '../lib/api';

const fmtQty = (n) => (Number.isFinite(+n) ? (+n).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—');

export default function OnHandReport() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], total_skus: 0, total_units: 0, generated_at: null });
  const [q, setQ] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [belowReorder, setBelowReorder] = useState(false);
  const [expandedSku, setExpandedSku] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_zero: includeZero, below_reorder: belowReorder };
      // Pass `q` only when it's non-empty so the backend's filter short-circuits otherwise.
      if (q.trim()) params.q = q.trim();
      const r = await getOnHandReport(params);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Debounced reload on filter change so typing in search doesn't hammer the server.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, includeZero, belowReorder]);

  const summary = useMemo(() => {
    const lowStock = data.rows.filter((r) => r.reorder_point && r.available <= r.reorder_point).length;
    const reservedUnits = data.rows.reduce((s, r) => s + (r.reserved || 0), 0);
    return { lowStock, reservedUnits };
  }, [data.rows]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="on-hand-report-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="overline">Reports</p>
            <h1 className="text-2xl font-bold">Stock On-Hand</h1>
            <p className="text-xs text-muted-foreground mt-1">
              All SKUs with current stock position, locations and per-UOM equivalents.
              {data.generated_at && (
                <span className="ml-2 font-mono">
                  · generated {new Date(data.generated_at).toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="on-hand-refresh-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
            <Button
              onClick={() => downloadOnHandReportCsv({ include_zero: includeZero, below_reorder: belowReorder, q })}
              className="bg-brand hover:bg-brand/90"
              data-testid="on-hand-csv-btn"
            >
              <ArrowDown size={14} className="mr-1" /> Download CSV
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard label="SKUs in report" value={data.total_skus} testId="summary-skus" />
          <SummaryCard label="Total units on-hand" value={fmtQty(data.total_units)} testId="summary-units" />
          <SummaryCard label="Reserved" value={fmtQty(summary.reservedUnits)} testId="summary-reserved" />
          <SummaryCard
            label="Below reorder point"
            value={summary.lowStock}
            warn={summary.lowStock > 0}
            testId="summary-low-stock"
          />
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="overline">Search</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SKU code or name…"
                className="pl-7 font-mono text-xs"
                data-testid="on-hand-search"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="filter-include-zero-label">
            <input
              type="checkbox"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              data-testid="filter-include-zero"
            />
            <span>Include zero-stock SKUs</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="filter-below-reorder-label">
            <input
              type="checkbox"
              checked={belowReorder}
              onChange={(e) => setBelowReorder(e.target.checked)}
              data-testid="filter-below-reorder"
            />
            <span>Below reorder point only</span>
          </label>
        </div>

        {/* Main table */}
        <div className="border border-border bg-card overflow-x-auto" data-testid="on-hand-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-right px-3 py-2">On hand</th>
                <th className="text-right px-3 py-2">Available</th>
                <th className="text-right px-3 py-2">Reserved</th>
                <th className="text-right px-3 py-2">Quarantine</th>
                <th className="text-left px-3 py-2">UOM</th>
                <th className="text-right px-3 py-2">Reorder pt</th>
                <th className="text-left px-3 py-2">Equivalents</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                  No SKUs match these filters. Try ticking &quot;Include zero-stock&quot; or clearing the search.
                </td></tr>
              )}
              {!loading && data.rows.map((r) => {
                const low = r.reorder_point && r.available <= r.reorder_point;
                const isExpanded = expandedSku === r.sku_id;
                return (
                  <React.Fragment key={r.sku_id}>
                    <tr
                      className={`border-t border-border hover:bg-muted/30 cursor-pointer ${low ? 'bg-amber-50/40' : ''}`}
                      onClick={() => setExpandedSku(isExpanded ? null : r.sku_id)}
                      data-testid={`on-hand-row-${r.sku_code}`}
                    >
                      <td className="px-3 py-2 font-mono">{r.sku_code}</td>
                      <td className="px-3 py-2 truncate max-w-[280px]">{r.name || '—'}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-bold">{fmtQty(r.on_hand)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtQty(r.available)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtQty(r.reserved)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtQty(r.quarantine)}</td>
                      <td className="px-3 py-2 font-mono uppercase">{r.base_uom}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {r.reorder_point > 0 ? r.reorder_point : '—'}
                        {low && <Warning size={12} weight="fill" className="inline ml-1 text-amber-600" aria-label="below reorder" />}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {r.uom_equivalents.length === 0 ? '—' : (
                          <span className="font-mono">
                            {r.uom_equivalents.map((e) => `${fmtQty(e.equivalent_qty)} ${e.code}`).join(' · ')}
                          </span>
                        )}
                      </td>
                    </tr>
                    {/* Expanded panel — locations */}
                    {isExpanded && (
                      <tr className="bg-muted/20" data-testid={`on-hand-detail-${r.sku_code}`}>
                        <td colSpan={9} className="px-3 py-3">
                          <div className="text-[10px] font-bold uppercase tracking-widest mb-2">Locations</div>
                          {r.locations.length === 0 ? (
                            <p className="text-xs opacity-60">No location rows.</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {r.locations.map((l) => (
                                <span
                                  key={l.location_id}
                                  className="inline-flex items-center gap-2 border border-border bg-card px-2 py-1 text-[11px] font-mono"
                                  data-testid={`on-hand-loc-${r.sku_code}-${l.location_code || l.location_id}`}
                                >
                                  <span className="font-bold">{l.location_code || l.location_id.slice(-6)}</span>
                                  {l.zone && <span className="text-muted-foreground">[{l.zone}]</span>}
                                  <span className="text-emerald-700">{fmtQty(l.qty)} {r.base_uom}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

const SummaryCard = ({ label, value, warn = false, testId }) => (
  <div className={`border ${warn ? 'border-amber-300 bg-amber-50/40' : 'border-border bg-card'} px-4 py-3`} data-testid={testId}>
    <p className="overline">{label}</p>
    <p className={`mt-1 text-2xl font-bold font-mono tabular-nums ${warn ? 'text-amber-700' : ''}`}>{value}</p>
  </div>
);
